import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ShieldCheck, Clock3, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { FadeIn, MaskedLines, Eyebrow } from "@/components/Reveal";
import { serviceOptions } from "@/data/services";
import { useTitle } from "@/hooks/use-title";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  organization: "",
  email: "",
  phone: "",
  service_required: "",
  project_description: "",
};

const fieldClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-coral-400 focus:ring-2 focus:ring-coral-100";

const Field = ({ label, required, children, htmlFor }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
    >
      {label} {required && <span className="text-coral-500">*</span>}
    </label>
    {children}
  </div>
);

export default function Contact() {
  useTitle("Contact — THREENNOVATORS LLP");
  const [params] = useSearchParams();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const s = params.get("service");
    if (s && serviceOptions.includes(s)) {
      setForm((f) => ({ ...f, service_required: s }));
    }
  }, [params]);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(initial);
      toast.success("Enquiry sent — our team will reach out shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not send your enquiry. Please email us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeIn y={12}>
              <Eyebrow>Contact</Eyebrow>
            </FadeIn>
            <h1
              className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-slate-900 sm:text-5xl"
              data-testid="contact-headline"
            >
              <MaskedLines
                lines={[
                  "Let’s discuss",
                  <>
                    your <span className="italic text-coral-500">project.</span>
                  </>,
                ]}
              />
            </h1>
            <FadeIn delay={0.45}>
              <p className="mt-7 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
                Tell us where you are and where you need to be. A specialist —
                not a sales rep — will respond.
              </p>
            </FadeIn>

            <FadeIn delay={0.55} className="mt-10 space-y-4">
              <a
                href="mailto:contact@threenn.com"
                data-testid="contact-email-card"
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-coral-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-coral-50 text-coral-500">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Email Us
                  </span>
                  <span className="block text-sm font-semibold text-slate-900 group-hover:text-coral-600">
                    contact@threenn.com
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-coral-50 text-coral-500">
                  <Clock3 className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Response Time
                  </span>
                  <span className="block text-sm font-semibold text-slate-900">
                    Within one business day
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-coral-50 text-coral-500">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Confidentiality
                  </span>
                  <span className="block text-sm font-semibold text-slate-900">
                    Every enquiry handled with 100% confidentiality
                  </span>
                </span>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3} className="lg:col-span-7">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50 sm:p-10">
              {sent ? (
                <div className="py-10 text-center" data-testid="contact-success-message">
                  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-coral-50">
                    <CheckCircle2 className="h-8 w-8 text-coral-500" />
                  </span>
                  <h2 className="mt-6 font-serif text-2xl text-slate-900 sm:text-3xl">
                    Enquiry received.
                  </h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
                    Thank you for reaching out to THREENNOVATORS LLP. A
                    specialist will review your brief and respond within one
                    business day. Your details remain strictly confidential.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another-button"
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:border-coral-400 hover:text-coral-600"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Name" required htmlFor="contact-name">
                      <input
                        id="contact-name"
                        type="text"
                        required
                        minLength={2}
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Dr. Jane Doe"
                        data-testid="contact-name-input"
                        className={fieldClass}
                      />
                    </Field>
                    <Field label="Organization" required htmlFor="contact-organization">
                      <input
                        id="contact-organization"
                        type="text"
                        required
                        value={form.organization}
                        onChange={update("organization")}
                        placeholder="University / Company / CRO"
                        data-testid="contact-organization-input"
                        className={fieldClass}
                      />
                    </Field>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Email" required htmlFor="contact-email">
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@institution.edu"
                        data-testid="contact-email-input"
                        className={fieldClass}
                      />
                    </Field>
                    <Field label="Phone" htmlFor="contact-phone">
                      <input
                        id="contact-phone"
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+1 555 000 0000 (optional)"
                        data-testid="contact-phone-input"
                        className={fieldClass}
                      />
                    </Field>
                  </div>
                  <Field label="Service Required" required htmlFor="contact-service">
                    <select
                      id="contact-service"
                      required
                      value={form.service_required}
                      onChange={update("service_required")}
                      data-testid="contact-service-select"
                      className={`${fieldClass} ${form.service_required ? "" : "text-slate-400"}`}
                    >
                      <option value="" disabled>
                        Select a service domain
                      </option>
                      {serviceOptions.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Project Description" required htmlFor="contact-description">
                    <textarea
                      id="contact-description"
                      required
                      minLength={10}
                      rows={5}
                      value={form.project_description}
                      onChange={update("project_description")}
                      placeholder="Tell us about your project — objective, current stage, target journal or regulator, and timeline."
                      data-testid="contact-description-input"
                      className={`${fieldClass} resize-y`}
                    />
                  </Field>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-coral-500 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-coral-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Submit Enquiry
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="text-xs leading-relaxed text-slate-400">
                    By submitting, you agree to be contacted about your enquiry.
                    Your information is never shared with third parties.
                  </p>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
